import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import type { InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
}

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  className,
  size = 'md',
  disabled,
  'aria-label': ariaLabel
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
  'aria-label'?: string;
}) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(
          'flex items-center justify-between gap-2 rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-3 text-sm transition-colors',
          size === 'sm' ? 'h-8' : 'h-9',
          'hover:border-[var(--text-subtle)]',
          'focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/25',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon>
          <ChevronDown className="size-4 shrink-0 text-[var(--text-subtle)]" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className="scroll-thin z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-y-auto rounded-xl border border-[var(--line)] bg-[var(--surface)] p-1 shadow-lg"
        >
          <SelectPrimitive.Viewport>
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm outline-none data-[highlighted]:bg-[var(--surface-hover)]"
              >
                <SelectPrimitive.ItemIndicator>
                  <Check className="size-4 text-[var(--accent)]" />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

/**
 * Calendar days use the native date input. It is keyboard accessible, localized
 * for free, and its value is already the `YYYY-MM-DD` string the API wants —
 * which sidesteps the Date round-tripping that caused v1's off-by-one-day bugs.
 */
export function DayInput({
  className,
  size = 'md',
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> & { size?: 'sm' | 'md' }) {
  return (
    <input
      type="date"
      className={cn(
        'rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-3 text-sm text-[var(--text)] transition-colors',
        size === 'sm' ? 'h-8' : 'h-9',
        'hover:border-[var(--text-subtle)]',
        'focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/25',
        'disabled:cursor-not-allowed disabled:opacity-50',
        '[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-50',
        'dark:[&::-webkit-calendar-picker-indicator]:invert',
        className
      )}
      {...props}
    />
  );
}
